/**
 * 🪨 **IS THIS SHEEP IN A PEN?** — D130, 2026-09-29.
 *
 * 🎯 **haugur:** *"you need 2 sheep in a pen for a lamb to be born"* — and then
 * *"look att fjárréttir reaseach in iceland, made from stone stacked in a
 * circle."* He chose **a built pen** over the tether peg, and **a 1.5 m
 * stacked-stone wall** (`grjotgardur`) over two plain blocks.
 *
 * ## 🔴 A PEN IS NOT A SHAPE. IT IS *CAN THE SHEEP WALK OUT.*
 *
 * Real pens are round (a fjárborg), rectangular (Þórkötlustaðarétt's
 * almenningur), a row of cells (Garðastekkur) or a wall against a rock face
 * (the Kiðjaberg stekkur). **So nothing here looks for a ring.** It walks
 * outward from the sheep, cell by cell, **with the sheep's own rules** — the
 * same `fits`, the same 1 m hop and 3 m drop `stepBody` uses — and if the
 * walk runs out of places to go before it runs out of room, the sheep is in a
 * pen. ✅ **A wall, a cliff, the sea or a shut gate all count, because a sheep
 * cannot cross any of them.** An open gate does not, because it is not solid.
 *
 * 🔴 **ONE COPY OF THE MOVEMENT RULE.** If this asked its own question — *is
 * the wall at least 1.5 m* — it would disagree with the sheep the day a
 * number moved, and say *pen* about a place a sheep strolls out of. ⚠️ So it
 * takes the species' {@link Fits} and {@link Physique} and nothing else.
 *
 * 📏 **The limits are real pens:** a lambakró is 2 × 2 m, and
 * Þórkötlustaðarétt — a whole communal rétt — is 41 × 37 m, about 1,500 m².
 */

import { blockIdForKey } from './blocks.ts';
import type { Fits, Ground, Physique, Vec3 } from './creature.ts';
import { baseBlockOf, stateOf, withState } from './states.ts';

/** 🪨 The stone wall — the item and the block share the key. */
export const WALL_BLOCK = 'grjotgardur';
/** 🚪 The pen gate — the item and the block share the key. */
export const GATE_BLOCK = 'hlid';

const GATE_ID = blockIdForKey(GATE_BLOCK);

/** 🚪 Is this block — in any state — a gate? */
export function isGate(id: number): boolean {
  return GATE_ID !== undefined && baseBlockOf(id) === GATE_ID;
}

/** 🚪 Is this gate open? `false` for anything that is not a gate. */
export function gateIsOpen(id: number): boolean {
  return isGate(id) && stateOf(id).open === true;
}

/**
 * 🚪 **The same gate, opened if it was shut and shut if it was open** — its
 * facing kept. `undefined` for anything that is not a gate.
 */
export function gateToggled(id: number): number | undefined {
  if (!isGate(id)) return undefined;
  return withState(id, { open: !gateIsOpen(id) });
}

/** 🐑 **The smallest pen, in square metres** — a lambakró, 2 × 2 m. */
export const PEN_MIN_CELLS = 4;
/** 🪨 **The largest, in square metres** — a whole rétt, with room to spare. */
export const PEN_MAX_CELLS = 2000;
/**
 * ⭕ **How far from the sheep a pen may reach, metres.** Past this the walk
 * gives up and calls it open country. 🔴 **The caller must load at least this
 * much ground plus a margin**: an unloaded cell answers SOLID (`Ground`), so a
 * sheep near the edge of what is loaded would otherwise look penned by the
 * edge of the world.
 */
export const PEN_REACH = 32;

/** 🪨 A pen a sheep is standing in. */
export interface Pen {
  /** How many square metres of it a sheep can reach. */
  readonly cells: number;
  /**
   * 🔑 **A name for this pen that any sheep in it arrives at** — the
   * west-most, then north-most, then lowest cell of the walk, as `x,y,z`.
   * 🔴 **Two sheep are in the SAME pen exactly when their anchors match**,
   * because each walk reaches every cell of it and picks the same one.
   */
  readonly anchor: string;
  /** Every cell of it, as `x,y,z` — where a penned sheep may wander. */
  readonly inside: ReadonlySet<string>;
}

const NEIGHBOURS: readonly (readonly [number, number])[] = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
];

/**
 * 🚶 **Where would a creature stand in the next cell over — or nowhere?**
 * `stepBody`'s own order: level first, then the hop up, then the nearest drop.
 */
function standIn(
  ground: Ground,
  fits: Fits,
  physique: Physique,
  x: number,
  y: number,
  z: number,
): number | null {
  const cx = x + 0.5;
  const cz = z + 0.5;
  if (fits(ground, { x: cx, y, z: cz })) return y;
  if (physique.step > 0 && fits(ground, { x: cx, y: y + physique.step, z: cz }))
    return y + physique.step;
  for (let d = 1; d <= (physique.drop ?? 0); d++) {
    if (fits(ground, { x: cx, y: y - d, z: cz })) return y - d;
  }
  return null;
}

/**
 * 🪨 **The pen this creature is standing in, or `null` for open country.**
 *
 * ⚠️ **`null` too when its own cell is somewhere it cannot stand** — never
 * guess a pen for an animal the world does not agree is standing.
 */
export function penAround(ground: Ground, fits: Fits, physique: Physique, at: Vec3): Pen | null {
  const sx = Math.floor(at.x);
  const sz = Math.floor(at.z);
  const sy = Math.round(at.y);
  if (!fits(ground, { x: sx + 0.5, y: sy, z: sz + 0.5 })) return null;

  const key = (x: number, y: number, z: number) => `${x},${y},${z}`;
  /* 🔴 Seen by COLUMN, not by cell: a sheep that could stand at two heights
     in one column is one square metre of pen, not two. */
  const seenColumn = new Set<string>();
  const inside = new Set<string>();
  const queue: [number, number, number][] = [[sx, sy, sz]];
  seenColumn.add(`${sx},${sz}`);
  let anchor: [number, number, number] = [sx, sy, sz];

  while (queue.length > 0) {
    const cell = queue.shift();
    if (cell === undefined) break;
    const [x, y, z] = cell;
    inside.add(key(x, y, z));
    if (inside.size > PEN_MAX_CELLS) return null;
    if (
      x < anchor[0] ||
      (x === anchor[0] && (z < anchor[2] || (z === anchor[2] && y < anchor[1])))
    ) {
      anchor = [x, y, z];
    }
    for (const [dx, dz] of NEIGHBOURS) {
      const nx = x + dx;
      const nz = z + dz;
      if (seenColumn.has(`${nx},${nz}`)) continue;
      const ny = standIn(ground, fits, physique, nx, y, nz);
      if (ny === null) continue;
      /* ⭕ Out past the reach: this is open country, and it got out. */
      if (Math.hypot(nx - sx, nz - sz) > PEN_REACH) return null;
      seenColumn.add(`${nx},${nz}`);
      queue.push([nx, ny, nz]);
    }
  }

  if (inside.size < PEN_MIN_CELLS) return null;
  return { cells: inside.size, anchor: key(...anchor), inside };
}
